/**
 * Deep-link survival across Cognito redirect sign-in.
 *
 * `redirectSignIn` is the origin only (see components/amplify/utils.ts), so a
 * user who opens a deep link without a live session is returned to `/` after
 * signing in. The intended path is stashed here immediately before the redirect
 * and replayed by the `/` route, which is the single place that decides where a
 * post-auth landing goes. Keeping that decision inside one render avoids racing
 * the `/` -> `/home` redirect.
 *
 * The read is deliberately separate from the clear: the reader runs in the
 * render phase, where a side effect would be double-invoked under StrictMode and
 * the second, already-cleared read would win.
 *
 * sessionStorage (not localStorage) so the value is scoped to the tab that
 * started the sign-in and cannot leak into a later session.
 */
const POST_AUTH_PATH_KEY = "gaia.postAuthPath";

/**
 * Only `/` is not a destination: it is the origin Cognito returns to, and the
 * root route decides where it leads. Every other path, `/home` included, is
 * somewhere the user asked for and must be restored.
 */
const isRestorable = (pathname: string) => pathname !== "/";

/**
 * Relative, non protocol-relative paths only — never navigate off-origin.
 * A backslash in the second position counts: URL parsing treats it as a slash,
 * so `/\\host` resolves off-origin exactly like `//host`.
 */
const isSafeInternalPath = (path: string) =>
  path.startsWith("/") && !/^\/[/\\]/.test(path);

/** Remember where the user was headed before a Cognito redirect sign-in. */
export const rememberPostAuthPath = (): void => {
  const { pathname, search, hash } = window.location;

  if (!isRestorable(pathname)) {
    return;
  }

  try {
    sessionStorage.setItem(POST_AUTH_PATH_KEY, `${pathname}${search}${hash}`);
  } catch {
    // Storage unavailable: the user lands on the default page, which is the
    // behaviour they got before this existed.
  }
};

/** Pure read — safe to call during render. Returns null when nothing is stashed. */
export const peekPostAuthPath = (): string | null => {
  try {
    const path = sessionStorage.getItem(POST_AUTH_PATH_KEY);

    if (!path) {
      return null;
    }

    return isSafeInternalPath(path) ? path : null;
  } catch {
    return null;
  }
};

/**
 * Drop the stash once it has been acted on, so an abandoned sign-in can redirect
 * at most one subsequent arrival at `/`.
 */
export const clearPostAuthPath = (): void => {
  try {
    sessionStorage.removeItem(POST_AUTH_PATH_KEY);
  } catch {
    // Nothing to do: a stash we cannot clear is discarded by the next read.
  }
};
